import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "error"
}

import { computed, inject, ref, toRefs, watch } from 'vue'
import CardInteractive from './CardInteractive.vue'
import HeatmapTable from '@/design-system/molecules/HeatmapTable.vue'
import { ColorScaleInterpolate } from '@/utils/colorScale'
import { FormatOptions } from '../formatter'
import { useDataSource } from '@/composables/useDataSources'
import { HeatmapResponse, HeatmapSplitBy } from '@/api-client/models/indoorclimate/Contracts'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { useI18n } from 'vue-i18n'
import { EntityLevel, useEntityLevel } from '@/composables/useEntityLevel'
import HeatmapPagination from '@/design-system/atoms/HeatmapPagination.vue'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { sharedDataKey } from '../../types'
import {
  formatDifferentMonths,
  formatSameMonthMultipleDays,
  formatSameMonthSingleDay
} from '@/utils/calendar-utils/formatters'
import { enUS, sv } from 'date-fns/locale'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { isSameDay, isSameMonth } from 'date-fns'
import { storeToRefs } from 'pinia'

type Titles = {
  [key in HeatmapSplitBy]: LocaleString
}

type Props = {
  title: string
  titles: Titles
  info?: string
  xSumLabel?: LocaleString
  ySumLabel?: LocaleString
  tooltipLabel: LocaleString
  dataFormat: FormatOptions
  timeFormat: FormatOptions
  colorScale: ColorScaleInterpolate
  dataSource: string
  enabledEntityLevels?: EntityLevel[]
  errorMessages?: Record<ErrorState, LocaleString>
  subMeasurementToTitle: Record<string, LocaleString>
  noReverse?: boolean
}

type ErrorState = 'NO_DATA' | 'INVALID_FILTER'


export default /*@__PURE__*/_defineComponent({
  __name: 'CardHeatmap',
  props: {
    title: {},
    titles: {},
    info: {},
    xSumLabel: {},
    ySumLabel: {},
    tooltipLabel: {},
    dataFormat: {},
    timeFormat: {},
    colorScale: {},
    dataSource: {},
    enabledEntityLevels: {},
    errorMessages: {},
    subMeasurementToTitle: {},
    noReverse: { type: Boolean }
  },
  setup(__props: any) {

const props = __props

const { locale } = useI18n()

const { dataSource } = toRefs(props)

const { entityLevel } = storeToRefs(useEntityLevel())

const heatmapData = useDataSource<HeatmapResponse>(dataSource)

const currentPage = ref(0)
const fullscreenHeatmap = ref(false)

const filters = useCurrentFeatureFilters()

const sharedData = inject(sharedDataKey)

const fullscreen = computed(() => sharedData?.fullscreen ?? false)

const correctFilter = computed(
  () =>
    (props.enabledEntityLevels && props.enabledEntityLevels.includes(entityLevel.value)) ||
    !props.enabledEntityLevels
)

const formatRange = (from: Date, to: Date) => {
  if (!isSameMonth(from, to)) {
    return formatDifferentMonths(from, to, { en: enUS, sv: sv }[locale.value])
  }
  if (!isSameDay(from, to)) {
    return formatSameMonthMultipleDays(from, to, { en: enUS, sv: sv }[locale.value])
  }
  return formatSameMonthSingleDay(from, { en: enUS, sv: sv }[locale.value])
}

const paginationTitle = computed(() => {
  const days = heatmapData.value?.data?.axes.x
  if (!days) {
    return ''
  }
  const fromStr = days[start.value]
  const toStr = days[end.value - 1]
  try {
    const from = fromStr ? new Date(fromStr) : undefined
    const to = toStr ? new Date(toStr) : undefined
    if (!from || !to) {
      return ''
    }
    return formatRange(from, to)
  } catch {
    return ''
  }
})

const itemsPerPage = computed(() => {
  const submitted = filters.value?.submitted
  if (!submitted) {
    return 0
  }
  if (fullscreenHeatmap.value || fullscreen.value) {
    return 20
  }
  if ('calendar' in submitted) {
    return 5
  }
  return 4
})

const daysCount = computed(() => heatmapData.value?.data?.axes.x.length ?? 0)

const maxPage = computed(() => Math.ceil(daysCount.value / itemsPerPage.value) - 1)
const rest = computed(() => daysCount.value % itemsPerPage.value)

const start = computed(() =>
  Math.max(
    (currentPage.value !== 0 ? rest.value : 0) +
      (currentPage.value - (rest.value === 0 ? 0 : 1)) * itemsPerPage.value,
    0
  )
)
const end = computed(() =>
  Math.min(
    rest.value + (currentPage.value + (rest.value === 0 ? 1 : 0)) * itemsPerPage.value,
    daysCount.value
  )
)

watch(
  maxPage,
  () => {
    if (!props.noReverse) currentPage.value = maxPage.value
  },
  { immediate: true }
)

function stepLeft() {
  currentPage.value--
}

function stepRight() {
  currentPage.value++
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(CardInteractive, {
    title: 
      _unref(heatmapData)?.data?.splitBy && _ctx.titles[_unref(heatmapData).data.splitBy][_unref(locale)]
        ? _ctx.titles[_unref(heatmapData)?.data?.splitBy][_unref(locale)]
        : _ctx.title
    ,
    info: _ctx.info,
    enableFullscreen: true,
    isLoading: _unref(heatmapData) && _unref(heatmapData).isLoading && correctFilter.value,
    showControls: true,
    headerDivider: true,
    onToggleFullscreen: _cache[0] || (_cache[0] = ($event: any) => (fullscreenHeatmap.value = $event))
  }, {
    controls: _withCtx(() => [
      _createVNode(HeatmapPagination, {
        totalItems: _unref(heatmapData)?.data?.axes.x.length ?? 0,
        itemsPerPage: itemsPerPage.value,
        currentPage: currentPage.value,
        pageTitle: paginationTitle.value,
        onStepLeft: stepLeft,
        onStepRight: stepRight
      }, null, 8, ["totalItems", "itemsPerPage", "currentPage", "pageTitle"])
    ]),
    default: _withCtx(() => [
      (_unref(heatmapData)?.data?.axes.x.length && correctFilter.value)
        ? (_openBlock(), _createBlock(HeatmapTable, {
            key: 0,
            fullscreen: fullscreenHeatmap.value,
            matrix: _unref(heatmapData)?.data,
            "color-scale": _ctx.colorScale,
            subMeasurementToTitle: _ctx.subMeasurementToTitle,
            xSumLabel: _ctx.xSumLabel?.[_unref(locale)],
            ySumLabel: _ctx.ySumLabel?.[_unref(locale)],
            tooltipLabel: _ctx.tooltipLabel[_unref(locale)],
            dataFormat: _ctx.dataFormat,
            timeFormat: _ctx.timeFormat,
            start: start.value,
            end: end.value
          }, null, 8, ["fullscreen", "matrix", "color-scale", "subMeasurementToTitle", "xSumLabel", "ySumLabel", "tooltipLabel", "dataFormat", "timeFormat", "start", "end"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_1, [
            (correctFilter.value)
              ? (_openBlock(), _createBlock(TextBase, {
                  key: 0,
                  text: _ctx.errorMessages?.NO_DATA[_unref(locale)],
                  type: "label"
                }, null, 8, ["text"]))
              : (_openBlock(), _createBlock(TextBase, {
                  key: 1,
                  text: _ctx.errorMessages?.INVALID_FILTER[_unref(locale)],
                  type: "label"
                }, null, 8, ["text"]))
          ]))
    ]),
    _: 1
  }, 8, ["title", "info", "isLoading"]))
}
}

})